import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AdminHeader, Card, StatusBadge, Empty, Btn, Modal, Input } from "@/components/admin/ui";
import { toast } from "sonner";
import { History } from "lucide-react";

export default function AdminInventory() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [adjust, setAdjust] = useState(null);
  const [qty, setQty] = useState(0);
  const [note, setNote] = useState("");

  const load = () => api.get("/admin/inventory").then(({ data }) => setRows(data.inventory));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/admin/inventory/adjust", { productId: adjust.productId, variantId: adjust.variantId, quantity: Number(qty), note });
      toast.success("Stock adjusted"); setAdjust(null); setQty(0); setNote(""); load();
    } catch { toast.error("Failed to adjust"); }
  };

  const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  return (
    <div>
      <AdminHeader title="Inventory" subtitle={`${rows.length} variants`} action={<Link to="/admin/inventory/history"><Btn variant="outline"><History className="h-4 w-4" /> History</Btn></Link>} />
      <div className="flex gap-2 mb-4">
        {[["all", "All"], ["in_stock", "In Stock"], ["low_stock", "Low Stock"], ["out_of_stock", "Out of Stock"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filter === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`inv-filter-${k}`}>{l}</button>
        ))}
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="p-3">Product</th><th className="p-3">SKU</th><th className="p-3">Stock</th><th className="p-3">Reserved</th><th className="p-3">Available</th><th className="p-3">Status</th><th className="p-3 text-right">Action</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr key={r.variantId} className="hover:bg-slate-50">
                  <td className="p-3 font-medium text-slate-900 max-w-[200px] line-clamp-1">{r.productName}</td>
                  <td className="p-3 font-mono text-xs">{r.sku}</td>
                  <td className="p-3 font-mono">{r.stock}</td>
                  <td className="p-3 font-mono text-slate-400">{r.reserved}</td>
                  <td className="p-3 font-mono font-semibold">{r.available}</td>
                  <td className="p-3"><StatusBadge status={r.status} /></td>
                  <td className="p-3 text-right"><Btn variant="outline" onClick={() => setAdjust(r)} data-testid={`adjust-${r.sku}`}>Adjust</Btn></td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <Empty />}
        </div>
      </Card>

      <Modal open={!!adjust} onClose={() => setAdjust(null)} title="Adjust Stock">
        {adjust && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{adjust.productName} · <span className="font-mono">{adjust.sku}</span> · Current: <b>{adjust.stock}</b></p>
            <Input label="Quantity change (+ add / − remove)" type="number" value={qty} onChange={(e) => setQty(e.target.value)} testid="adjust-qty" />
            <Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} testid="adjust-note" />
            <Btn variant="accent" className="w-full" onClick={save} data-testid="save-adjust-btn">Apply Adjustment</Btn>
          </div>
        )}
      </Modal>
    </div>
  );
}
